// Generic pipeline card. Renders running / complete / failed / cancelled /
// skipped states with an accent border colored from CSS tokens.

import { clsx } from "clsx";
import type { PipelineId } from "../types/contract";
import type { CardState } from "../state/store";

// Pipeline accent colour is now resolved via the `pipeline-card`
// CSS class + a `data-pipeline` attribute (see styles/tokens.css).
// Keeping the colour off inline `style` props lets the production CSP
// drop 'unsafe-inline' from style-src.

const LABEL: Record<PipelineId, string> = {
  reverse_image: "REVERSE",
  ai_gemini: "GEMINI",
  ai_claude: "CLAUDE",
  ai_gpt: "GPT",
  ocr: "OCR",
  meta: "META",
  deep_mode: "DEEP",
  minimap: "MINIMAP",
};

export interface PipelineCardProps {
  pipeline: PipelineId;
  state: CardState;
  /** Wall clock for elapsed-since-start computation in tests. */
  elapsedMs?: number;
  children?: React.ReactNode;
}

export function PipelineCard({
  pipeline,
  state,
  elapsedMs,
  children,
}: PipelineCardProps) {
  const isRunning = state.status === "running";
  const elapsedS =
    state.status === "running" && elapsedMs != null
      ? Math.max(0, Math.floor((elapsedMs - state.startedAtMs) / 1000))
      : null;
  return (
    <div
      data-testid={`card-${pipeline}`}
      data-pipeline={pipeline}
      className={clsx(
        "pipeline-card relative rounded border bg-white/5 px-3 py-2",
        "border-[color:var(--pipe-accent)]",
        isRunning && "animate-pulse",
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold tracking-wider text-[color:var(--pipe-accent)]">
          {LABEL[pipeline]}
        </span>
        <span className="flex-1" />
        <StatusBadge state={state} elapsedS={elapsedS} />
      </div>
      {children && <div className="mt-1 text-xs text-fg">{children}</div>}
    </div>
  );
}

function StatusBadge({
  state,
  elapsedS,
}: {
  state: CardState;
  elapsedS: number | null;
}) {
  switch (state.status) {
    case "idle":
      return <span className="text-fg-muted text-[10px]">idle</span>;
    case "running":
      return (
        <span className="text-[10px] text-[color:var(--running)]">
          running{elapsedS != null ? ` · ${elapsedS}s` : ""}
        </span>
      );
    case "complete":
      return (
        <span className="text-[10px] text-[color:var(--ok)]">
          {(state.latencyMs / 1000).toFixed(1)}s
        </span>
      );
    case "failed":
      return (
        <span className="text-[10px] text-[color:var(--err)]" title={state.errorMessage}>
          {state.errorCategory}
        </span>
      );
    case "cancelled":
      return <span className="text-[10px] text-fg-muted">cancelled</span>;
    case "skipped":
      return <span className="text-[10px] text-fg-muted">skipped</span>;
  }
}
